import React, { useEffect, useRef, useState } from 'react';
import { Pause } from 'lucide-react';
import type { LstMotivoParada } from '../../types';
import {
  LST_MOTIVO_PARADA_LABELS,
  LST_MOTIVO_PARADA_OPTIONS,
  STOPPED_BADGE_STYLE,
} from '../../utils/kamishibaiStatus';

interface ProcessStoppedBadgeProps {
  motivo?: LstMotivoParada | null;
  size?: 'sm' | 'md';
  className?: string;
}

export const ProcessStoppedBadge: React.FC<ProcessStoppedBadgeProps> = ({
  motivo,
  size = 'md',
  className = '',
}) => {
  const label = motivo ? LST_MOTIVO_PARADA_LABELS[motivo] : 'Parado';
  const sizeCls = size === 'sm' ? 'h-6 px-1.5 text-[9px]' : 'h-8 px-2 text-[10px]';

  return (
    <span
      title={label}
      className={`inline-flex items-center gap-0.5 rounded-md font-bold ring-1 ${STOPPED_BADGE_STYLE} ${sizeCls} ${className}`}
      aria-label={`Processo parado: ${label}`}
    >
      <Pause size={size === 'sm' ? 10 : 12} strokeWidth={2.5} />
      <span className="max-w-[72px] truncate">{label}</span>
    </span>
  );
};

interface ProcessStoppedControlProps {
  active: boolean;
  motivo: LstMotivoParada | null;
  disabled?: boolean;
  /** Abre o dropdown de motivos ao montar (ex.: após clicar em Linha Parada na sidebar). */
  defaultOpen?: boolean;
  onSelect: (motivo: LstMotivoParada) => void;
  onClear?: () => void;
}

/** Botão "Linha Parada" + dropdown de motivo — rodapé da sidebar contextual. */
export const ProcessStoppedControl: React.FC<ProcessStoppedControlProps> = ({
  active,
  motivo,
  disabled = false,
  defaultOpen = false,
  onSelect,
  onClear,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Linha / processo parado"
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors disabled:opacity-50 ${
          active
            ? 'border-slate-500 bg-slate-500 text-white shadow-sm'
            : 'border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
        }`}
      >
        <Pause size={11} strokeWidth={2.5} />
        <span className="hidden sm:inline">Linha Parada</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full z-50 mt-1 min-w-[160px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {LST_MOTIVO_PARADA_OPTIONS.map((key) => (
            <li key={key}>
              <button
                type="button"
                role="option"
                aria-selected={motivo === key}
                onClick={() => {
                  onSelect(key);
                  setOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-[11px] text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {LST_MOTIVO_PARADA_LABELS[key]}
              </button>
            </li>
          ))}
          {active && onClear && (
            <li className="border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-[11px] text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Limpar exceção
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
